import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
    Grid,
    Card,
    CardContent,
    CardMedia,
    CardActions,
    Button,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Chip,
    Box
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CloudUpload as UploadIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from "@mui/icons-material";
import * as Actions from "actions";

const AdminSystemModelsMain = (props) => {
    const [showDialog, setShowDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', 'image'
    const [selectedModel, setSelectedModel] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        gender: 'unisex',
        sort_order: 0
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [includeInactive, setIncludeInactive] = useState(false);

    const {
        models, isLoading, adminGetSystemModels, adminCreateSystemModel,
        adminUpdateSystemModel, adminUpdateSystemModelImage, adminDeleteSystemModel,
        adminToggleSystemModelActive
    } = props;

    const loadModels = () => {
        adminGetSystemModels(includeInactive);
    };

    useEffect(() => {
        loadModels();
    }, []);

    const handleOpenDialog = (mode, model = null) => {
        if (mode === 'edit' && model) {
            setShowDialog(true);
            setDialogMode(mode);
            setSelectedModel(model);
            setFormData({
                name: model.name,
                description: model.description || '',
                gender: model.gender,
                sort_order: model.sort_order || 0
            });
        } else if (mode === 'image' && model) {
            setShowDialog(true);
            setDialogMode(mode);
            setSelectedModel(model);
            setImageFile(null);
            setImagePreview(null);
        } else {
            setShowDialog(true);
            setDialogMode(mode);
            setSelectedModel(null);
            setFormData({
                name: '',
                description: '',
                gender: 'unisex',
                sort_order: 0
            });
            setImageFile(null);
            setImagePreview(null);
        }
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
        setDialogMode('create');
        setSelectedModel(null);
        setFormData({
            name: '',
            description: '',
            gender: 'unisex',
            sort_order: 0
        });
        setImageFile(null);
        setImagePreview(null);
    };

    const handleFormChange = (field, value) => {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = () => {
        if (dialogMode === 'create') {
            if (!formData.name || !imageFile) {
                alert('Please provide model name and image');
                return;
            }
            
            const data = new FormData();
            data.append('name', formData.name);
            data.append('description', formData.description);
            data.append('gender', formData.gender);
            data.append('sort_order', formData.sort_order);
            data.append('image', imageFile);

            adminCreateSystemModel(data, () => {
                handleCloseDialog();
                loadModels();
            });
        } else if (dialogMode === 'edit') {
            adminUpdateSystemModel(selectedModel._id, formData, () => {
                handleCloseDialog();
                loadModels();
            });
        } else if (dialogMode === 'image') {
            if (!imageFile) {
                alert('Please select an image');
                return;
            }

            const data = new FormData();
            data.append('image', imageFile);

            adminUpdateSystemModelImage(selectedModel._id, data, () => {
                handleCloseDialog();
                loadModels();
            });
        }
    };

    const handleDelete = (modelId) => {
        if (window.confirm('Are you sure you want to delete this model?')) {
            adminDeleteSystemModel(modelId, () => {
                loadModels();
            });
        }
    };

    const handleToggleActive = (modelId, currentStatus) => {
        adminToggleSystemModelActive(modelId, !currentStatus, () => {
            loadModels();
        });
    };

    const handleToggleIncludeInactive = () => {
        setIncludeInactive(!includeInactive);
        setTimeout(() => {
            adminGetSystemModels(!includeInactive);
        }, 0);
    };

    const renderDialog = () => {
        return (
            <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogMode === 'create' && 'Add New Model Photo'}
                    {dialogMode === 'edit' && 'Edit Model Details'}
                    {dialogMode === 'image' && 'Update Model Image'}
                </DialogTitle>
                <DialogContent>
                    {(dialogMode === 'create' || dialogMode === 'edit') && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
                            <TextField
                                label="Model Name"
                                value={formData.name}
                                onChange={(e) => handleFormChange('name', e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleFormChange('description', e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    value={formData.gender}
                                    onChange={(e) => handleFormChange('gender', e.target.value)}
                                >
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="unisex">Unisex</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Sort Order"
                                type="number"
                                value={formData.sort_order}
                                onChange={(e) => handleFormChange('sort_order', e.target.value)}
                                fullWidth
                            />
                        </div>
                    )}
                    
                    {(dialogMode === 'create' || dialogMode === 'image') && (
                        <div style={{ marginTop: '16px' }}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="model-image-upload"
                                type="file"
                                onChange={handleImageSelect}
                            />
                            <label htmlFor="model-image-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<UploadIcon />}
                                    fullWidth
                                >
                                    {imageFile ? 'Change Image' : 'Select Image'}
                                </Button>
                            </label>
                            
                            {imagePreview && (
                                <Box mt={2}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                                    />
                                </Box>
                            )}
                            
                            {dialogMode === 'image' && selectedModel && !imagePreview && (
                                <Box mt={2}>
                                    <Typography variant="caption" color="textSecondary">
                                        Current Image:
                                    </Typography>
                                    <img
                                        src={selectedModel.image?.full_size_cdn?.secure_url || selectedModel.image?.cdn?.secure_url}
                                        alt={selectedModel.name}
                                        style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', marginTop: '8px' }}
                                    />
                                </Box>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <div style={{ padding: '24px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">System Model Photos</Typography>
                <Box>
                    {/*
                    <Button
                        variant="outlined"
                        color={includeInactive ? "primary" : "default"}
                        onClick={handleToggleIncludeInactive}
                        style={{ marginRight: '16px' }}
                    >
                        {includeInactive ? 'Hide' : 'Show'} Inactive
                    </Button>
                    */}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog('create')}
                    >
                        Add Model
                    </Button>
                </Box>
            </Box>

            {isLoading && models.length === 0 ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {models.map((model) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={model._id}>
                            <Card>
                                <CardMedia
                                    component="img"
                                    height="300"
                                    image={model.image?.full_size_cdn?.secure_url || model.image?.cdn?.secure_url}
                                    alt={model.name}
                                    style={{ objectFit: 'cover' }}
                                />
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="h6" component="div">
                                            {model.name}
                                        </Typography>
                                        <Chip
                                            label={model.is_active ? 'Active' : 'Inactive'}
                                            color={model.is_active ? 'primary' : 'default'}
                                            size="small"
                                        />
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        {model.description || 'No description'}
                                    </Typography>
                                    <Box display="flex" gap={1} mt={1}>
                                        <Chip label={model.gender} size="small" />
                                        <Chip label={`Order: ${model.sort_order}`} size="small" />
                                    </Box>
                                </CardContent>
                                <CardActions>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleToggleActive(model._id, model.is_active)}
                                        title={model.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                        {model.is_active ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenDialog('edit', model)}
                                        title="Edit Details"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenDialog('image', model)}
                                        title="Change Image"
                                    >
                                        <UploadIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDelete(model._id)}
                                        title="Delete"
                                        color="secondary"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {!isLoading && models.length === 0 && (
                <Box display="flex" justifyContent="center" p={4}>
                    <Typography variant="body1" color="textSecondary">
                        No model photos found. Click "Add Model" to create one.
                    </Typography>
                </Box>
            )}

            {renderDialog()}
        </div>
    );
};

// Mapping Props and State.
const adminSystemModelsMain = connect(
    ({ adminSystemModels, bootupInfo }) => ({
        bootupInfo,
        models: adminSystemModels.models || [],
        isLoading: adminSystemModels.isLoading || false,
        reqStatus: adminSystemModels.reqStatus,
        serviceError: adminSystemModels.serviceError,
    }),
    (dispatch) => bindActionCreators({ ...Actions }, dispatch),
)(AdminSystemModelsMain);

export { adminSystemModelsMain as default };
